/**
 * Procedural chiptune / synth music. Each track is composed deterministically from a small spec
 * (tempo, key, progression, instruments, drum style) and scheduled with WebAudio lookahead.
 *
 *   playMusic('town')          start (cross-fades from whatever is playing; waits for unlock)
 *   playMusic('victory')       stingers play once and stop
 *   stopMusic(1.5)             fade out
 *   musicForMap(theme.music)   pick a track from a map theme id
 *
 * Output goes to `audio.musicOut`, so the music volume setting applies.
 */
import { audio } from './Audio';

export type MusicTrack = 'menu' | 'town' | 'training' | 'military' | 'snow' | 'jungle' | 'neon' | 'desert' | 'castle' | 'island' | 'farm' | 'victory' | 'defeat';

export const MUSIC_TRACKS: MusicTrack[] = ['menu', 'town', 'training', 'military', 'snow', 'jungle', 'neon', 'desert', 'castle', 'island', 'farm', 'victory', 'defeat'];

type InstKind = 'pulse' | 'tri' | 'saw' | 'supersaw' | 'bell' | 'pluck' | 'organ' | 'marimba' | 'steel' | 'sine' | 'harpsi';

interface Inst {
  kind: InstKind;
  vol: number;
  duty?: number;
  cutoff?: number;
  /** Seconds for percussive decay (bell, pluck, marimba...). */
  decay?: number;
  /** Echo send 0..1. */
  echo?: number;
}

type BassStyle = 'drive' | 'bounce' | 'march' | 'walk' | 'drone' | 'calypso' | 'synth';
type ArpStyle = 'up' | 'updown' | 'broken' | 'alberti';
type DrumStyle = 'rock' | 'four' | 'march' | 'shuffle' | 'tribal' | 'calypso' | 'soft' | 'heartbeat' | 'desert' | 'castle' | 'sleigh';

interface TrackSpec {
  bpm: number;
  swing: number;
  /** Tonic MIDI note (chords/bass are built around it). */
  root: number;
  scale: number[];
  melodyScale?: number[];
  /** Chord roots per bar, as scale degrees. */
  prog: number[];
  lead: Inst | null;
  bass: Inst & { style: BassStyle };
  arp: (Inst & { style: ArpStyle; rate: 8 | 16 }) | null;
  pad: Inst | null;
  drums: { style: DrumStyle; vol: number };
  /** Lead note density 0..1. */
  density: number;
  echo: number;
  seed: number;
}

const MAJOR = [0, 2, 4, 5, 7, 9, 11];
const MINOR = [0, 2, 3, 5, 7, 8, 10];
const DORIAN = [0, 2, 3, 5, 7, 9, 10];
const MIXO = [0, 2, 4, 5, 7, 9, 10];
const LYDIAN = [0, 2, 4, 6, 7, 9, 11];
const PHRYG_DOM = [0, 1, 4, 5, 7, 8, 10];
const HARM_MINOR = [0, 2, 3, 5, 7, 8, 11];
const MAJ_PENT = [0, 2, 4, 7, 9];
const MIN_PENT = [0, 3, 5, 7, 10];

const TRACKS: Record<Exclude<MusicTrack, 'victory' | 'defeat'>, TrackSpec> = {
  menu: {
    bpm: 118, swing: 0, root: 60, scale: MAJOR, prog: [0, 4, 5, 3],
    lead: { kind: 'pulse', duty: 0.25, vol: 0.16, echo: 0.3 },
    bass: { kind: 'tri', vol: 0.34, style: 'drive' },
    arp: { kind: 'pulse', duty: 0.125, vol: 0.06, style: 'up', rate: 16 },
    pad: null, drums: { style: 'rock', vol: 0.55 }, density: 0.8, echo: 0.28, seed: 11,
  },
  town: {
    bpm: 112, swing: 0.14, root: 53, scale: MIXO, prog: [0, 3, 0, 6],
    lead: { kind: 'pulse', duty: 0.5, vol: 0.12, echo: 0.2 },
    bass: { kind: 'tri', vol: 0.34, style: 'bounce' },
    arp: null, pad: { kind: 'organ', vol: 0.05 }, drums: { style: 'shuffle', vol: 0.45 }, density: 0.7, echo: 0.22, seed: 23,
  },
  training: {
    bpm: 100, swing: 0.08, root: 55, scale: MAJOR, melodyScale: MAJ_PENT, prog: [0, 5, 3, 4],
    lead: { kind: 'tri', vol: 0.2, echo: 0.3 },
    bass: { kind: 'tri', vol: 0.3, style: 'bounce' },
    arp: { kind: 'pluck', vol: 0.08, style: 'broken', rate: 8, decay: 0.25 },
    pad: null, drums: { style: 'soft', vol: 0.4 }, density: 0.55, echo: 0.3, seed: 37,
  },
  military: {
    bpm: 104, swing: 0, root: 50, scale: DORIAN, prog: [0, 6, 5, 6],
    lead: { kind: 'pulse', duty: 0.5, vol: 0.11, echo: 0.2 },
    bass: { kind: 'saw', vol: 0.2, cutoff: 700, style: 'march' },
    arp: null, pad: { kind: 'saw', vol: 0.035, cutoff: 1200 }, drums: { style: 'march', vol: 0.55 }, density: 0.6, echo: 0.2, seed: 41,
  },
  snow: {
    bpm: 84, swing: 0, root: 52, scale: LYDIAN, prog: [0, 1, 5, 4],
    lead: { kind: 'bell', vol: 0.16, decay: 0.9, echo: 0.45 },
    bass: { kind: 'tri', vol: 0.28, style: 'drone' },
    arp: { kind: 'bell', vol: 0.05, decay: 0.5, style: 'broken', rate: 8, echo: 0.4 },
    pad: { kind: 'organ', vol: 0.045 }, drums: { style: 'sleigh', vol: 0.3 }, density: 0.45, echo: 0.4, seed: 53,
  },
  jungle: {
    bpm: 116, swing: 0.06, root: 57, scale: MINOR, melodyScale: MIN_PENT, prog: [0, 3, 4, 0],
    lead: { kind: 'marimba', vol: 0.16, decay: 0.3, echo: 0.2 },
    bass: { kind: 'tri', vol: 0.26, style: 'walk' },
    arp: { kind: 'marimba', vol: 0.05, decay: 0.15, style: 'updown', rate: 16 },
    pad: null, drums: { style: 'tribal', vol: 0.42 }, density: 0.7, echo: 0.2, seed: 61,
  },
  neon: {
    bpm: 124, swing: 0, root: 47, scale: MINOR, prog: [0, 5, 2, 6],
    lead: { kind: 'pulse', duty: 0.25, vol: 0.12, echo: 0.45 },
    bass: { kind: 'saw', vol: 0.2, cutoff: 900, style: 'synth' },
    arp: { kind: 'saw', vol: 0.05, cutoff: 2400, style: 'up', rate: 16, echo: 0.3 },
    pad: { kind: 'supersaw', vol: 0.04, cutoff: 1800 }, drums: { style: 'four', vol: 0.55 }, density: 0.65, echo: 0.4, seed: 71,
  },
  desert: {
    bpm: 96, swing: 0.05, root: 52, scale: PHRYG_DOM, prog: [0, 1, 0, 6],
    lead: { kind: 'pluck', vol: 0.2, decay: 0.45, echo: 0.3 },
    bass: { kind: 'tri', vol: 0.3, style: 'drone' },
    arp: null, pad: { kind: 'organ', vol: 0.03 }, drums: { style: 'desert', vol: 0.5 }, density: 0.7, echo: 0.3, seed: 83,
  },
  castle: {
    bpm: 100, swing: 0, root: 50, scale: HARM_MINOR, prog: [0, 3, 4, 0],
    lead: { kind: 'pulse', duty: 0.5, vol: 0.1, echo: 0.25 },
    bass: { kind: 'tri', vol: 0.3, style: 'walk' },
    arp: { kind: 'harpsi', vol: 0.07, decay: 0.2, style: 'alberti', rate: 16 },
    pad: { kind: 'organ', vol: 0.05 }, drums: { style: 'castle', vol: 0.5 }, density: 0.6, echo: 0.25, seed: 97,
  },
  island: {
    bpm: 108, swing: 0.1, root: 48, scale: MAJOR, melodyScale: MAJ_PENT, prog: [0, 3, 4, 0],
    lead: { kind: 'steel', vol: 0.2, decay: 0.5, echo: 0.2 },
    bass: { kind: 'tri', vol: 0.32, style: 'calypso' },
    arp: null, pad: null, drums: { style: 'calypso', vol: 0.5 }, density: 0.75, echo: 0.2, seed: 101,
  },
  farm: {
    bpm: 72, swing: 0, root: 45, scale: MINOR, prog: [0, 5, 3, 4],
    lead: { kind: 'bell', vol: 0.12, decay: 1.2, echo: 0.55 },
    bass: { kind: 'tri', vol: 0.3, style: 'drone' },
    arp: null, pad: { kind: 'organ', vol: 0.05 }, drums: { style: 'heartbeat', vol: 0.5 }, density: 0.3, echo: 0.5, seed: 113,
  },
};

/** Map theme music id (or any hint) -> track. Unknown ids fall back to 'town'. */
export function musicForMap(musicId?: string | null): MusicTrack {
  const id = (musicId ?? '').toLowerCase();
  if ((MUSIC_TRACKS as string[]).includes(id)) return id as MusicTrack;
  const aliases: [RegExp, MusicTrack][] = [
    [/frost|ice|arctic|winter|snow/, 'snow'],
    [/temple|ruin|jungle|forest|sunken/, 'jungle'],
    [/neon|roof|city|cyber|night/, 'neon'],
    [/dune|desert|sand|oasis/, 'desert'],
    [/castle|keep|duel|pit|medieval/, 'castle'],
    [/island|beach|tropic|br\b|royale/, 'island'],
    [/farm|harvest|zombie|corrupt/, 'farm'],
    [/crane|yard|military|base|outpost|industrial|factory/, 'military'],
    [/train|practice|boot/, 'training'],
    [/menu|lobby|hub/, 'menu'],
  ];
  for (const [re, t] of aliases) if (re.test(id)) return t;
  return 'town';
}

// Deterministic composition -----------------------------------------------------------------

class Rand {
  constructor(private s: number) {}
  next(): number {
    this.s = (Math.imul(this.s, 1664525) + 1013904223) >>> 0;
    return this.s / 4294967296;
  }
  int(n: number): number {
    return Math.floor(this.next() * n);
  }
  pick<T>(a: T[]): T {
    return a[this.int(a.length)];
  }
}

const freq = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

/** The octave of `root` that starts inside [lo, lo + 12). */
function octaveInto(root: number, lo: number): number {
  return root + 12 * Math.ceil((lo - root) / 12);
}

function noteOf(root: number, scale: number[], degree: number): number {
  const L = scale.length;
  const oct = Math.floor(degree / L);
  const idx = ((degree % L) + L) % L;
  return root + oct * 12 + scale[idx];
}

/** Nearest note of `scale` to a MIDI note (for melodies over pentatonic scales). */
function snap(m: number, root: number, scale: number[]): number {
  let best = m;
  let bd = 99;
  for (let o = -2; o <= 2; o++)
    for (const s of scale) {
      const n = root + 12 * Math.round((m - root) / 12) + o * 12 + s;
      const d = Math.abs(n - m);
      if (d < bd) {
        bd = d;
        best = n;
      }
    }
  return best;
}

interface NoteEv {
  step: number;
  len: number;
  midi: number;
  vel: number;
}

const RHYTHMS = [
  'x.x.x-x.x-x.x-..',
  'x--.x.x.x--.x.x.',
  'x.xx.x.xx-x.x...',
  'x---x.x.x---x...',
  'x.x.x.x.x-x-x-..',
  'x-.xx-.xx-x.x-..',
  'x...x.x.x...x.x.',
  'x-x-x-..x.x.x---',
];
const SPARSE = ['x-----..x-----..', 'x---....x-x-....', 'x-------x---....', 'x-----x.x-------'];

/** Generates an 8-bar looped melody as chord-relative degrees realized per bar. */
function composeLead(spec: TrackSpec, r: Rand): NoteEv[] {
  const bars = spec.prog.length * 2;
  const dense = spec.density > 0.5;
  // Two-bar phrase A and B as rhythms + chord-relative scale offsets.
  const phrase = (): { rh: string; off: number[] } => {
    const rh = (dense ? r.pick(RHYTHMS) : r.pick(SPARSE)) + (dense ? r.pick(RHYTHMS) : r.pick(SPARSE));
    const off: number[] = [];
    let cur = r.pick([0, 2, 4]);
    for (let i = 0; i < rh.length; i++) {
      if (rh[i] !== 'x') {
        off.push(cur);
        continue;
      }
      const strong = i % 4 === 0;
      if (strong) {
        const targets = [cur - (cur % 2), cur + 2 - (cur % 2)].map((t) => Math.max(-3, Math.min(5, t)));
        cur = r.pick(targets);
      } else cur = Math.max(-3, Math.min(6, cur + r.pick([-2, -1, -1, 1, 1, 2])));
      off.push(cur);
    }
    return { rh, off };
  };
  const A = phrase();
  const B = phrase();
  const order = [A, A, B, A];
  const out: NoteEv[] = [];
  const mscale = spec.melodyScale ?? spec.scale;
  const leadRoot = octaveInto(spec.root, 60);
  for (let p = 0; p < order.length; p++) {
    const ph = order[p];
    for (let i = 0; i < 32; i++) {
      if (ph.rh[i] !== 'x') continue;
      const bar = p * 2 + Math.floor(i / 16);
      const chord = spec.prog[bar % spec.prog.length];
      let off = ph.off[i];
      // Cadence: the loop's last note lands on the tonic.
      if (p === order.length - 1 && i >= 24) off = 0;
      let m = noteOf(leadRoot, spec.scale, chord + off);
      if (mscale !== spec.scale) m = snap(m, spec.root, mscale);
      let len = 1;
      while (i + len < 32 && ph.rh[i + len] === '-') len++;
      out.push({ step: bar * 16 + (i % 16), len, midi: m, vel: i % 4 === 0 ? 1 : 0.8 });
    }
  }
  return out.filter((n) => n.step < bars * 16);
}

function composeBass(spec: TrackSpec): NoteEv[] {
  const out: NoteEv[] = [];
  const bars = spec.prog.length * 2;
  const pat: Record<BassStyle, [number, number, number][]> = {
    // [step, length, chord tone: 0 root, 1 fifth, 2 octave, 3 third]
    drive: [[0, 2, 0], [2, 2, 0], [4, 2, 2], [6, 2, 0], [8, 2, 0], [10, 2, 0], [12, 2, 2], [14, 2, 1]],
    bounce: [[0, 3, 0], [4, 3, 1], [8, 3, 0], [12, 3, 1]],
    march: [[0, 3, 0], [4, 2, 0], [8, 3, 1], [12, 2, 0], [14, 2, 1]],
    walk: [[0, 4, 0], [4, 4, 3], [8, 4, 1], [12, 4, 2]],
    drone: [[0, 16, 0]],
    calypso: [[0, 3, 0], [3, 3, 0], [6, 2, 1], [8, 3, 1], [11, 3, 2], [14, 2, 1]],
    synth: [[0, 1, 0], [2, 1, 2], [4, 1, 0], [6, 1, 2], [8, 1, 0], [10, 1, 2], [12, 1, 0], [14, 1, 2]],
  };
  const bassRoot = octaveInto(spec.root, 36);
  for (let bar = 0; bar < bars; bar++) {
    const chord = spec.prog[bar % spec.prog.length];
    const rootNote = noteOf(bassRoot, spec.scale, chord);
    for (const [st, len, tone] of pat[spec.bass.style]) {
      const m = tone === 0 ? rootNote : tone === 1 ? noteOf(bassRoot, spec.scale, chord + 4) : tone === 2 ? rootNote + 12 : noteOf(bassRoot, spec.scale, chord + 2);
      out.push({ step: bar * 16 + st, len, midi: m, vel: st === 0 ? 1 : 0.85 });
    }
  }
  return out;
}

function composeArp(spec: TrackSpec): NoteEv[] {
  const a = spec.arp;
  if (!a) return [];
  const out: NoteEv[] = [];
  const bars = spec.prog.length * 2;
  const seqs: Record<ArpStyle, number[]> = { up: [0, 2, 4, 7], updown: [0, 2, 4, 7, 4, 2], broken: [0, 4, 2, 4], alberti: [0, 4, 2, 4] };
  const seq = seqs[a.style];
  const every = a.rate === 16 ? 1 : 2;
  for (let bar = 0; bar < bars; bar++) {
    const chord = spec.prog[bar % spec.prog.length];
    let k = 0;
    for (let st = 0; st < 16; st += every) {
      const deg = chord + seq[k++ % seq.length];
      out.push({ step: bar * 16 + st, len: every, midi: noteOf(octaveInto(spec.root, 60), spec.scale, deg), vel: st % 4 === 0 ? 1 : 0.7 });
    }
  }
  return out;
}

function composePad(spec: TrackSpec): NoteEv[][] {
  if (!spec.pad) return [];
  const bars = spec.prog.length * 2;
  const out: NoteEv[][] = [];
  for (let bar = 0; bar < bars; bar++) {
    const chord = spec.prog[bar % spec.prog.length];
    out.push([0, 2, 4].map((d) => ({ step: bar * 16, len: 16, midi: noteOf(octaveInto(spec.root, 48), spec.scale, chord + d), vel: 1 })));
  }
  return out;
}

type DrumHit = 'kick' | 'snare' | 'hat' | 'open' | 'clap' | 'tom' | 'tomlo' | 'shaker' | 'timpani' | 'crash' | 'bell';
const DRUMS: Record<DrumStyle, Partial<Record<DrumHit, string>>> = {
  rock: { kick: 'x.......x.x.....', snare: '....x.......x...', hat: 'x.x.x.x.x.x.x.x.' },
  four: { kick: 'x...x...x...x...', clap: '....x.......x...', hat: '..x...x...x...x.', shaker: 'x.x.x.x.x.x.x.x.' },
  march: { kick: 'x.......x.......', snare: '....x..x....x.xx', tom: '..............x.' },
  shuffle: { kick: 'x.....x.x.......', snare: '....x.......x...', hat: 'x.xx.xx.xx.xx.x.' },
  tribal: { kick: 'x.......x..x....', tom: 'x..x..x...x.x...', tomlo: '..x.....x.....x.', shaker: 'x.x.x.x.x.x.x.x.' },
  calypso: { kick: 'x..x..x.x..x..x.', snare: '....x.......x...', shaker: 'xxxxxxxxxxxxxxxx' },
  soft: { kick: 'x.......x.......', hat: '..x...x...x...x.', shaker: '....x.......x...' },
  heartbeat: { kick: 'x.x.............', hat: '..............x.' },
  desert: { tom: 'x..x.x..x..x.x..', tomlo: 'x.......x.......', shaker: '..x...x...x...x.' },
  castle: { timpani: 'x.......x.......', snare: '............x.xx', hat: 'x...x...x...x...' },
  sleigh: { kick: 'x.......x.......', bell: 'x.x.x.x.x.x.x.x.' },
};

// Instruments ---------------------------------------------------------------------------------

const waveCache = new WeakMap<BaseAudioContext, Map<string, PeriodicWave>>();
function wave(ctx: BaseAudioContext, key: string, harmonics: (n: number) => number, count = 32): PeriodicWave {
  let m = waveCache.get(ctx);
  if (!m) waveCache.set(ctx, (m = new Map()));
  let w = m.get(key);
  if (!w) {
    const real = new Float32Array(count + 1);
    const imag = new Float32Array(count + 1);
    for (let n = 1; n <= count; n++) imag[n] = harmonics(n);
    w = ctx.createPeriodicWave(real, imag);
    m.set(key, w);
  }
  return w;
}
const pulseWave = (ctx: BaseAudioContext, duty: number): PeriodicWave => wave(ctx, `pulse${duty}`, (n) => (2 / (n * Math.PI)) * Math.sin(n * Math.PI * duty));
const organWave = (ctx: BaseAudioContext): PeriodicWave => wave(ctx, 'organ', (n) => ({ 1: 1, 2: 0.6, 3: 0.25, 4: 0.35, 6: 0.12, 8: 0.15 })[n] ?? 0);
const marimbaWave = (ctx: BaseAudioContext): PeriodicWave => wave(ctx, 'marimba', (n) => ({ 1: 1, 4: 0.28, 10: 0.06 })[n] ?? 0);
const steelWave = (ctx: BaseAudioContext): PeriodicWave => wave(ctx, 'steel', (n) => ({ 1: 1, 2: 0.55, 3: 0.3, 4: 0.18, 5: 0.1 })[n] ?? 0);

const noiseCache = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseCache.get(ctx);
  if (!b) {
    b = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = b.getChannelData(0);
    let s = 22222;
    for (let i = 0; i < d.length; i++) {
      s = (Math.imul(s, 1103515245) + 12345) >>> 0;
      d[i] = s / 2147483648 - 1;
    }
    noiseCache.set(ctx, b);
  }
  return b;
}

/** One playing track: builds note events and schedules them ahead of time. */
class Sequencer {
  readonly out: GainNode;
  private echoIn: GainNode;
  private busLP: BiquadFilterNode;
  private lead: NoteEv[];
  private bass: NoteEv[];
  private arp: NoteEv[];
  private pad: NoteEv[][];
  private stepDur: number;
  private loopSteps: number;
  private nextStep = 0;
  private startTime: number;
  private stopped = false;
  readonly finite: boolean;
  endTime = Infinity;

  constructor(
    private ctx: BaseAudioContext,
    dest: AudioNode,
    private spec: TrackSpec,
    start: number,
    private stinger: NoteEv[] | null = null,
    private stingerPad: NoteEv[] = [],
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(dest);
    // Everything passes a gentle low-pass so the chiptune timbres stay soft on small speakers.
    this.busLP = ctx.createBiquadFilter();
    this.busLP.type = 'lowpass';
    this.busLP.frequency.value = 9000;
    this.busLP.connect(this.out);
    // Echo send.
    this.echoIn = ctx.createGain();
    const delay = ctx.createDelay(2);
    const beat = 60 / spec.bpm;
    delay.delayTime.value = beat * 0.75;
    const fb = ctx.createGain();
    fb.gain.value = spec.echo;
    const damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2600;
    this.echoIn.connect(delay);
    delay.connect(damp);
    damp.connect(fb);
    fb.connect(delay);
    damp.connect(this.busLP);
    this.stepDur = beat / 4;
    this.loopSteps = spec.prog.length * 2 * 16;
    this.startTime = start;
    this.finite = !!stinger;
    if (stinger) {
      this.lead = stinger;
      this.bass = [];
      this.arp = [];
      this.pad = stingerPad.length ? [stingerPad] : [];
      let last = 0;
      for (const n of [...stinger, ...stingerPad]) last = Math.max(last, n.step + n.len);
      this.loopSteps = last + 8;
      this.endTime = start + this.loopSteps * this.stepDur + 1.5;
    } else {
      const r = new Rand(spec.seed);
      this.lead = spec.lead ? composeLead(spec, r) : [];
      this.bass = composeBass(spec);
      this.arp = composeArp(spec);
      this.pad = composePad(spec);
    }
  }

  fadeTo(level: number, at: number, dur: number): void {
    const g = this.out.gain;
    g.cancelScheduledValues(at);
    g.setValueAtTime(g.value, at);
    g.linearRampToValueAtTime(level, at + Math.max(0.01, dur));
  }

  stop(at: number, fade: number): void {
    if (this.stopped) return;
    this.stopped = true;
    this.fadeTo(0, at, fade);
    this.endTime = Math.min(this.endTime, at + fade + 0.1);
    const ctx = this.ctx;
    setTimeout(() => this.out.disconnect(), Math.max(0, (at + fade - ctx.currentTime) * 1000) + 400);
  }

  private stepTime(step: number): number {
    const swing = step % 2 === 1 ? this.spec.swing * this.stepDur : 0;
    return this.startTime + step * this.stepDur + swing;
  }

  /** Schedules everything that starts before `until` (seconds, context time). */
  schedule(until: number): void {
    if (this.stopped) return;
    while (this.stepTime(this.nextStep) < until) {
      const s = this.nextStep++;
      if (this.finite && s >= this.loopSteps) {
        this.stopped = true;
        return;
      }
      const local = s % this.loopSteps;
      const loopIndex = Math.floor(s / this.loopSteps);
      const t = this.stepTime(s);
      const sd = this.stepDur;
      for (const n of this.lead) if (n.step === local) this.note(this.spec.lead ?? { kind: 'pulse', duty: 0.25, vol: 0.16 }, n.midi, t, n.len * sd, n.vel);
      for (const n of this.bass) if (n.step === local) this.note(this.spec.bass, n.midi, t, n.len * sd * 0.92, n.vel);
      if (this.spec.arp) for (const n of this.arp) if (n.step === local) this.note(this.spec.arp, n.midi + (loopIndex % 2 === 1 && local % 64 >= 32 ? 12 : 0), t, n.len * sd * 0.9, n.vel);
      for (const chord of this.pad) for (const n of chord) if (n.step === local && this.spec.pad) this.note(this.spec.pad, n.midi, t, n.len * sd, 1);
      if (!this.finite) this.drums(local, t);
    }
  }

  private drums(local: number, t: number): void {
    const pat = DRUMS[this.spec.drums.style];
    const st = local % 16;
    const bar = Math.floor(local / 16);
    const fill = bar % 4 === 3 && st >= 12;
    for (const [hit, p] of Object.entries(pat) as [DrumHit, string][]) {
      let on = p[st] === 'x';
      if (fill && (hit === 'snare' || hit === 'tom')) on = st % 1 === 0;
      if (on) this.drum(hit, t, this.spec.drums.vol * (st % 4 === 0 ? 1 : 0.8));
    }
    if (local === 0 && this.spec.drums.style !== 'heartbeat' && this.spec.drums.style !== 'sleigh') this.drum('crash', t, this.spec.drums.vol * 0.4);
  }

  private env(g: GainNode, t: number, peak: number, attack: number, hold: number, release: number): void {
    const p = g.gain;
    p.setValueAtTime(0, t);
    p.linearRampToValueAtTime(peak, t + attack);
    p.setValueAtTime(peak, t + attack + hold);
    p.linearRampToValueAtTime(0, t + attack + hold + release);
  }

  private decayEnv(g: GainNode, t: number, peak: number, decay: number): number {
    const p = g.gain;
    p.setValueAtTime(0, t);
    p.linearRampToValueAtTime(peak, t + 0.004);
    p.exponentialRampToValueAtTime(0.0008, t + decay * 4);
    return t + decay * 4 + 0.02;
  }

  note(inst: Inst, midi: number, t: number, dur: number, vel: number): void {
    const ctx = this.ctx;
    const f = freq(midi);
    const g = ctx.createGain();
    g.connect(this.busLP);
    if (inst.echo) {
      const send = ctx.createGain();
      send.gain.value = inst.echo;
      g.connect(send);
      send.connect(this.echoIn);
    }
    const vol = inst.vol * vel;
    const oscs: OscillatorNode[] = [];
    const mk = (fr: number): OscillatorNode => {
      const o = ctx.createOscillator();
      o.frequency.value = fr;
      oscs.push(o);
      return o;
    };
    let end = t + dur + 0.1;
    switch (inst.kind) {
      case 'pulse': {
        const o = mk(f);
        o.setPeriodicWave(pulseWave(ctx, inst.duty ?? 0.5));
        o.connect(g);
        this.env(g, t, vol, 0.004, Math.max(0.01, dur * 0.8), 0.05);
        break;
      }
      case 'tri':
      case 'sine': {
        const o = mk(f);
        o.type = inst.kind === 'tri' ? 'triangle' : 'sine';
        o.connect(g);
        this.env(g, t, vol, 0.006, Math.max(0.01, dur * 0.85), 0.06);
        break;
      }
      case 'saw':
      case 'supersaw': {
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = inst.cutoff ?? 1500;
        lp.Q.value = 0.8;
        lp.connect(g);
        const detunes = inst.kind === 'supersaw' ? [-9, 0, 9] : [0];
        for (const dt of detunes) {
          const o = mk(f);
          o.type = 'sawtooth';
          o.detune.value = dt;
          o.connect(lp);
        }
        this.env(g, t, vol / Math.sqrt(detunes.length), inst.kind === 'supersaw' ? 0.08 : 0.006, Math.max(0.01, dur * 0.85), inst.kind === 'supersaw' ? 0.25 : 0.06);
        end += 0.25;
        break;
      }
      case 'organ': {
        const o = mk(f);
        o.setPeriodicWave(organWave(ctx));
        o.connect(g);
        this.env(g, t, vol, 0.05, Math.max(0.01, dur * 0.9), 0.2);
        end += 0.2;
        break;
      }
      case 'bell': {
        const o = mk(f);
        const o2 = mk(f * 2.76);
        const g2 = ctx.createGain();
        g2.gain.value = 0.3;
        o.connect(g);
        o2.connect(g2);
        g2.connect(g);
        end = this.decayEnv(g, t, vol, (inst.decay ?? 0.8) / 4);
        break;
      }
      case 'marimba':
      case 'steel': {
        const o = mk(f);
        o.setPeriodicWave(inst.kind === 'marimba' ? marimbaWave(ctx) : steelWave(ctx));
        o.connect(g);
        end = this.decayEnv(g, t, vol, (inst.decay ?? 0.3) / 4);
        break;
      }
      case 'pluck':
      case 'harpsi': {
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.Q.value = inst.kind === 'pluck' ? 4 : 1;
        lp.frequency.setValueAtTime(Math.min(9000, f * 8), t);
        lp.frequency.exponentialRampToValueAtTime(Math.max(200, f * 1.2), t + (inst.decay ?? 0.3));
        lp.connect(g);
        const o = mk(f);
        if (inst.kind === 'pluck') o.type = 'sawtooth';
        else o.setPeriodicWave(pulseWave(ctx, 0.125));
        o.connect(lp);
        end = this.decayEnv(g, t, vol, (inst.decay ?? 0.3) / 3);
        break;
      }
    }
    for (const o of oscs) {
      o.start(t);
      o.stop(end);
    }
    oscs[0].onended = () => g.disconnect();
  }

  drum(hit: DrumHit, t: number, vol: number): void {
    const ctx = this.ctx;
    const g = ctx.createGain();
    g.connect(this.busLP);
    let last: AudioScheduledSourceNode | null = null;
    let lastEnd = 0;
    const track = (src: AudioScheduledSourceNode, end: number): void => {
      if (end < lastEnd) return;
      if (last) last.onended = null;
      last = src;
      lastEnd = end;
      src.onended = () => g.disconnect();
    };
    const noise = (hp: number, type: BiquadFilterType, q = 0.7, dur = 0.2): void => {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer(ctx);
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = hp;
      f.Q.value = q;
      src.connect(f);
      f.connect(g);
      src.start(t, Math.random() * 0.5);
      src.stop(t + dur);
      track(src, t + dur);
    };
    const tone = (f0: number, f1: number, sweep: number, dur: number, type: OscillatorType = 'sine', level = 1): void => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(f0, t);
      o.frequency.exponentialRampToValueAtTime(f1, t + sweep);
      const og = ctx.createGain();
      og.gain.value = level;
      o.connect(og);
      og.connect(g);
      o.start(t);
      o.stop(t + dur);
      track(o, t + dur);
    };
    const p = g.gain;
    switch (hit) {
      case 'kick':
        tone(150, 42, 0.11, 0.35);
        this.decayEnv(g, t, vol * 0.9, 0.06);
        break;
      case 'snare':
        noise(1500, 'highpass', 0.7, 0.2);
        tone(190, 140, 0.05, 0.1, 'triangle', 0.5);
        this.decayEnv(g, t, vol * 0.45, 0.035);
        break;
      case 'clap':
        noise(1200, 'bandpass', 1.2, 0.2);
        p.setValueAtTime(0, t);
        for (const [dt, a] of [[0, 1], [0.012, 0.7], [0.024, 1]] as const) {
          p.setValueAtTime(vol * 0.5 * a, t + dt);
          p.linearRampToValueAtTime(0.02, t + dt + 0.01);
        }
        p.exponentialRampToValueAtTime(0.001, t + 0.18);
        break;
      case 'hat':
        noise(7500, 'highpass', 0.7, 0.06);
        this.decayEnv(g, t, vol * 0.18, 0.012);
        break;
      case 'open':
        noise(7000, 'highpass', 0.7, 0.3);
        this.decayEnv(g, t, vol * 0.16, 0.06);
        break;
      case 'shaker':
        noise(5500, 'bandpass', 1.5, 0.08);
        this.decayEnv(g, t, vol * 0.12, 0.015);
        break;
      case 'tom':
        tone(220, 110, 0.1, 0.3);
        this.decayEnv(g, t, vol * 0.55, 0.05);
        break;
      case 'tomlo':
        tone(140, 75, 0.12, 0.35);
        this.decayEnv(g, t, vol * 0.6, 0.06);
        break;
      case 'timpani':
        tone(95, 72, 0.2, 0.8);
        noise(300, 'lowpass', 0.7, 0.3);
        this.decayEnv(g, t, vol * 0.7, 0.12);
        break;
      case 'crash':
        noise(3500, 'highpass', 0.7, 1.4);
        this.decayEnv(g, t, vol * 0.14, 0.28);
        break;
      case 'bell':
        tone(2637, 2637, 0.01, 0.12, 'sine', 0.6);
        noise(8000, 'highpass', 0.7, 0.05);
        this.decayEnv(g, t, vol * 0.12, 0.02);
        break;
    }
  }
}

// Stingers ------------------------------------------------------------------------------------

function stinger(track: 'victory' | 'defeat'): { spec: TrackSpec; lead: NoteEv[]; pad: NoteEv[] } {
  const n = (step: number, len: number, midi: number, vel = 1): NoteEv => ({ step, len, midi, vel });
  if (track === 'victory') {
    const spec: TrackSpec = { ...TRACKS.menu, bpm: 132, echo: 0.3, lead: { kind: 'pulse', duty: 0.25, vol: 0.3, echo: 0.3 }, pad: { kind: 'supersaw', vol: 0.12, cutoff: 3000 } };
    return {
      spec,
      lead: [n(0, 2, 72), n(2, 2, 76), n(4, 2, 79), n(6, 2, 84), n(8, 1, 79), n(9, 1, 84), n(10, 1, 88), n(12, 12, 91), n(12, 12, 84, 0.7)],
      pad: [n(12, 14, 60), n(12, 14, 64), n(12, 14, 67), n(12, 14, 48)],
    };
  }
  const spec: TrackSpec = { ...TRACKS.farm, bpm: 84, echo: 0.4, lead: { kind: 'tri', vol: 0.22, echo: 0.4 }, pad: { kind: 'organ', vol: 0.07 } };
  return {
    spec,
    lead: [n(0, 4, 75), n(4, 4, 74), n(8, 4, 72), n(12, 4, 71), n(16, 12, 67)],
    pad: [n(16, 14, 48), n(16, 14, 55), n(16, 14, 63)],
  };
}

// Playback ------------------------------------------------------------------------------------

let current: { track: MusicTrack; seq: Sequencer } | null = null;
let wanted: MusicTrack | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
const fading: Sequencer[] = [];

function lookahead(): number {
  return typeof document !== 'undefined' && document.hidden ? 1.5 : 0.25;
}

function tick(): void {
  const ctx = audio.ctx;
  if (!ctx) return;
  const until = ctx.currentTime + lookahead();
  if (current) {
    current.seq.schedule(until);
    if (current.seq.finite && ctx.currentTime > current.seq.endTime) {
      current = null;
      audio.setMusic(null);
    }
  }
  for (let i = fading.length - 1; i >= 0; i--) {
    fading[i].schedule(until);
    if (ctx.currentTime > fading[i].endTime) fading.splice(i, 1);
  }
  if (!current && !fading.length && timer) {
    clearInterval(timer);
    timer = null;
  }
}

function makeSequencer(ctx: BaseAudioContext, dest: AudioNode, track: MusicTrack, start: number): Sequencer {
  if (track === 'victory' || track === 'defeat') {
    const s = stinger(track);
    return new Sequencer(ctx, dest, s.spec, start, s.lead, s.pad);
  }
  return new Sequencer(ctx, dest, TRACKS[track], start);
}

function start(track: MusicTrack, fade: number): void {
  const ctx = audio.ctx;
  const out = audio.musicOut;
  if (!ctx || !out) return;
  const t = ctx.currentTime + 0.06;
  if (current) {
    const old = current.seq;
    old.stop(t, fade);
    fading.push(old);
  }
  const seq = makeSequencer(ctx, out, track, t + (current ? Math.min(0.4, fade * 0.3) : 0));
  const stingerTrack = track === 'victory' || track === 'defeat';
  seq.fadeTo(1, t, stingerTrack ? 0.03 : Math.max(0.3, fade));
  current = { track, seq };
  // The engine stops the previous handle when a new one is set; only act if still current.
  audio.setMusic({
    stop: () => {
      if (current?.seq === seq) stopMusic(0.6);
    },
  });
  if (!timer) timer = setInterval(tick, 25);
  tick();
}

/**
 * Starts a music track (cross-fading from the current one over `fade` seconds). Unknown names
 * are resolved with musicForMap; null stops. Before the audio context is unlocked the request
 * is remembered and starts on unlock. 'victory'/'defeat' play once.
 */
export function playMusic(track: MusicTrack | string | null, opts: { fade?: number } = {}): void {
  if (!track) {
    stopMusic(opts.fade);
    return;
  }
  const t = (MUSIC_TRACKS as string[]).includes(track) ? (track as MusicTrack) : musicForMap(track);
  const fade = opts.fade ?? 1.5;
  if (!audio.ctx) {
    const first = wanted === null;
    wanted = t;
    if (first)
      audio.onUnlock(() => {
        const w = wanted;
        wanted = null;
        if (w) start(w, 0.8);
      });
    return;
  }
  if (current && current.track === t && !current.seq.finite) return;
  start(t, fade);
}

/** Fades the music out. */
export function stopMusic(fade = 1.2): void {
  wanted = null;
  const ctx = audio.ctx;
  if (!current || !ctx) return;
  const old = current.seq;
  current = null;
  old.stop(ctx.currentTime + 0.02, fade);
  fading.push(old);
  audio.setMusic(null);
}

/** The track currently playing (null when silent or after a stinger ends). */
export function currentMusic(): MusicTrack | null {
  return current?.track ?? null;
}

/** Renders a track offline (tests / previews). */
export async function renderMusicOffline(track: MusicTrack, seconds: number, sampleRate = 44100): Promise<AudioBuffer> {
  const ctx = new OfflineAudioContext(1, Math.floor(seconds * sampleRate), sampleRate);
  const master = ctx.createGain();
  master.connect(ctx.destination);
  const seq = makeSequencer(ctx, master, track, 0.05);
  seq.fadeTo(1, 0, 0.01);
  seq.schedule(seconds);
  return ctx.startRendering();
}
